import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import { NAV_SECTIONS, BOTTOM_SECTIONS, PINNED_SHORTCUTS, ROLE_PINNED_SHORTCUTS } from 'config/navConfig';
import { ROLE_PROFILES, DEMO_USER } from 'config/roleConfig';
import { useRole } from 'context/RoleContext';

const LOGO = '/assets/images/SeedDrive_Black_-1772706977187.png';

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Collect all navigable paths within a section (handles 2-level nesting). */
function getAllChildPaths(section) {
  if (!section.children) return [];
  return section.children.flatMap((child) =>
    child.children ? child.children.map((gc) => gc.path) : child.path ? [child.path] : []
  );
}

/** True if the current pathname is at or under the given path. */
function isPathActive(pathname, path) {
  if (!path) return false;
  return pathname === path || pathname.startsWith(path + '/');
}

/** True if any child of a section is active. */
function isSectionActive(section, pathname) {
  return getAllChildPaths(section).some((p) => isPathActive(pathname, p));
}

/** Build initial accordion open-state from config + localStorage. Active sections win. */
function buildInitialGroupOpen(sections, pathname) {
  let stored = {};
  try {
    const raw = localStorage.getItem('sd_accordion_state_v2');
    if (raw) stored = JSON.parse(raw);
  } catch (_) {}
  return Object.fromEntries(
    sections
      .filter((s) => s.children)
      .map((s) => [s.id, isSectionActive(s, pathname) || stored[s.id] || false])
  );
}

// ─── Sub-item renderers ───────────────────────────────────────────────────────

function NavItem({ item, isCollapsed, hoveredItem, setHoveredItem, handleNavClick }) {
  const location = useLocation();
  const active = isPathActive(location.pathname, item.path);
  const badgeCount = item.badge || 0;

  return (
    <div key={item.id} className="relative">
      <button
        onClick={() => handleNavClick(item.path)}
        onMouseEnter={() => setHoveredItem(item.id)}
        onMouseLeave={() => setHoveredItem(null)}
        className={`sidebar-nav-item w-full text-left ${active ? 'active' : ''}`}
        aria-current={active ? 'page' : undefined}
        title={isCollapsed ? item.label : undefined}
      >
        <span className="flex-shrink-0 relative">
          <Icon
            name={item.icon}
            size={20}
            color={active ? 'var(--color-nav-active-fg)' : 'rgba(255,255,255,0.58)'}
            strokeWidth={active ? 2.5 : 2}
          />
          {isCollapsed && badgeCount > 0 && (
            <span
              className="absolute -top-1 -right-1 flex items-center justify-center w-4 h-4 rounded-full text-white font-caption font-semibold"
              style={{ fontSize: '0.625rem', background: 'var(--color-error)' }}
            >
              {badgeCount > 9 ? '9+' : badgeCount}
            </span>
          )}
        </span>
        <span className="nav-label flex-1 flex items-center justify-between">
          <span>{item.label}</span>
          {badgeCount > 0 && (
            <span
              className="flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full text-white font-caption font-semibold ml-2"
              style={{ fontSize: '0.6875rem', background: 'var(--color-error)' }}
            >
              {badgeCount > 99 ? '99+' : badgeCount}
            </span>
          )}
        </span>
      </button>
      {isCollapsed && hoveredItem === item.id && (
        <div
          className="absolute left-full top-1/2 -translate-y-1/2 ml-3 px-3 py-1.5 bg-popover text-popover-foreground rounded-md shadow-md font-caption text-sm whitespace-nowrap pointer-events-none"
          style={{ zIndex: 'var(--z-dropdown)' }}
        >
          {item.label}
          {badgeCount > 0 && <span className="ml-2 text-error font-semibold">{badgeCount}</span>}
        </div>
      )}
    </div>
  );
}

function ChildItem({ child, handleNavClick }) {
  const location = useLocation();
  const active = isPathActive(location.pathname, child.path);
  return (
    <button
      key={child.id}
      onClick={() => handleNavClick(child.path)}
      className={`sidebar-nav-item w-full text-left ${active ? 'active' : ''}`}
      aria-current={active ? 'page' : undefined}
    >
      <span className="flex-shrink-0">
        <Icon
          name={child.icon}
          size={17}
          color={active ? 'var(--color-nav-active-fg)' : 'rgba(255,255,255,0.58)'}
          strokeWidth={active ? 2.5 : 2}
        />
      </span>
      <span className="nav-label text-sm">{child.label}</span>
    </button>
  );
}

// ─── Main Sidebar ─────────────────────────────────────────────────────────────

export default function Sidebar({
  isCollapsed = false,
  isOpen = false,
  onToggleCollapse,
  onClose,
  userRole = 'investment_team',
  notificationCounts = {},
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [hoveredItem, setHoveredItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef(null);
  const [toastMsg, setToastMsg] = useState('');

  const pinnedShortcuts = ROLE_PINNED_SHORTCUTS[userRole] || PINNED_SHORTCUTS;

  // ── Accordion state — keyed by section.id, not label ──────────────────────
  const [groupOpen, setGroupOpen] = useState(() =>
    buildInitialGroupOpen(NAV_SECTIONS, location.pathname)
  );

  // Keep active section open when navigating
  useEffect(() => {
    NAV_SECTIONS.filter((s) => s.children).forEach((section) => {
      if (isSectionActive(section, location.pathname)) {
        setGroupOpen((prev) => ({ ...prev, [section.id]: true }));
      }
    });
  }, [location.pathname]);

  // Persist accordion state by section id
  useEffect(() => {
    localStorage.setItem('sd_accordion_state_v2', JSON.stringify(groupOpen));
  }, [groupOpen]);

  // ── Search history ─────────────────────────────────────────────────────────
  const [searchHistory, setSearchHistory] = useState(() => {
    try {
      const raw = localStorage.getItem('sd_search_history');
      if (raw) return JSON.parse(raw);
    } catch (_) {}
    return [];
  });

  useEffect(() => {
    localStorage.setItem('sd_search_history', JSON.stringify(searchHistory));
  }, [searchHistory]);

  // ── Permission filter ──────────────────────────────────────────────────────
  const canAccess = (permission) => permission?.includes(userRole);

  // ── Flat searchable items from config ─────────────────────────────────────
  const allSearchableItems = NAV_SECTIONS.flatMap((section) => {
    if (!section.children) return canAccess(section.permission) ? [section] : [];
    return section.children.flatMap((child) => {
      if (!canAccess(child.permission)) return [];
      if (child.children) {
        return child.children.filter((gc) => canAccess(gc.permission));
      }
      return [child];
    });
  }).concat(BOTTOM_SECTIONS.filter((s) => canAccess(s.permission)));

  const searchResults = searchQuery.trim().length > 0
    ? allSearchableItems.filter((item) =>
        item.label?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  // ── Handlers ──────────────────────────────────────────────────────────────
  const showToast = (msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(''), 2500);
  };

  const handleNavClick = (path) => {
    navigate(path);
    if (onClose) onClose();
  };

  const handleLogout = () => navigate('/');

  const handleSearchToggle = () => {
    if (isCollapsed) return;
    setSearchOpen((prev) => {
      if (!prev) setTimeout(() => searchInputRef.current?.focus(), 50);
      else setSearchQuery('');
      return !prev;
    });
  };

  const handleSearchNav = (path) => {
    if (searchQuery.trim().length > 0) {
      const trimmed = searchQuery.trim();
      setSearchHistory((prev) => [trimmed, ...prev.filter((h) => h !== trimmed)].slice(0, 8));
    }
    setSearchQuery('');
    setSearchOpen(false);
    handleNavClick(path);
  };

  const handleClearHistory = () => {
    setSearchHistory([]);
    localStorage.removeItem('sd_search_history');
    showToast('Search history cleared');
  };

  const toggleGroup = (sectionId) =>
    setGroupOpen((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }));

  const roleProfile = ROLE_PROFILES[userRole] || ROLE_PROFILES.investment_team;
  const { activeContext } = useRole();

  // ── Renderers ─────────────────────────────────────────────────────────────

  const renderSection = (section) => {
    if (!canAccess(section.permission)) return null;

    // Standalone item (no children)
    if (!section.children) {
      return (
        <NavItem
          key={section.id}
          item={section}
          isCollapsed={isCollapsed}
          hoveredItem={hoveredItem}
          setHoveredItem={setHoveredItem}
          handleNavClick={handleNavClick}
        />
      );
    }

    // Group — filter accessible children
    const accessibleChildren = section.children.filter((c) => canAccess(c.permission));
    if (accessibleChildren.length === 0) return null;

    const anyChildActive = isSectionActive(section, location.pathname);
    const isOpen = groupOpen[section.id] || false;

    // Collapsed mode: flatten all leaf children as icons
    if (isCollapsed) {
      return accessibleChildren.flatMap((child) => {
        if (child.children) {
          return child.children
            .filter((gc) => canAccess(gc.permission))
            .map((gc) => (
              <NavItem
                key={gc.id}
                item={gc}
                isCollapsed={isCollapsed}
                hoveredItem={hoveredItem}
                setHoveredItem={setHoveredItem}
                handleNavClick={handleNavClick}
              />
            ));
        }
        return (
          <NavItem
            key={child.id}
            item={child}
            isCollapsed={isCollapsed}
            hoveredItem={hoveredItem}
            setHoveredItem={setHoveredItem}
            handleNavClick={handleNavClick}
          />
        );
      });
    }

    return (
      <div key={section.id}>
        <button
          onClick={() => toggleGroup(section.id)}
          onMouseEnter={() => setHoveredItem(section.id)}
          onMouseLeave={() => setHoveredItem(null)}
          className={`sidebar-nav-item w-full text-left ${anyChildActive && !isOpen ? 'active' : ''}`}
          aria-expanded={isOpen}
        >
          <span className="flex-shrink-0">
            <Icon
              name={section.icon}
              size={20}
              color={anyChildActive ? 'var(--color-sidebar-active-fg)' : 'currentColor'}
              strokeWidth={anyChildActive ? 2.5 : 2}
            />
          </span>
          <span className="nav-label flex-1 flex items-center justify-between">
            <span className="flex items-center gap-2" style={{ color: '#e0e0e0' }}>
              {section.label}
              {section.tag === 'beta' && (
                <span className="font-caption rounded px-1 py-0.5 flex-shrink-0" style={{ fontSize: '0.55rem', letterSpacing: '0.06em', textTransform: 'uppercase', background: 'rgba(224,224,224,0.18)', color: 'rgba(224,224,224,0.70)' }}>Beta</span>
              )}
            </span>
            <Icon name={isOpen ? 'ChevronDown' : 'ChevronRight'} size={14} color="rgba(255,255,255,0.35)" strokeWidth={2} />
          </span>
        </button>

        {isOpen && (
          <div className="mt-0.5 ml-3 pl-3 border-l border-border space-y-0.5">
            {accessibleChildren.map((child) => {
              // Child is itself a nested group (e.g. Token Governance)
              if (child.children) {
                const accessibleGrandchildren = child.children.filter((gc) => canAccess(gc.permission));
                if (accessibleGrandchildren.length === 0) return null;
                const anyGcActive = accessibleGrandchildren.some((gc) => isPathActive(location.pathname, gc.path));
                const childOpen = groupOpen[child.id] || anyGcActive;

                return (
                  <div key={child.id}>
                    <button
                      onClick={() => setGroupOpen((prev) => ({ ...prev, [child.id]: !prev[child.id] }))}
                      className={`sidebar-nav-item w-full text-left ${anyGcActive && !childOpen ? 'active' : ''}`}
                      aria-expanded={childOpen}
                    >
                      <span className="flex-shrink-0">
                        <Icon
                          name={child.icon}
                          size={17}
                          color={anyGcActive ? 'var(--color-sidebar-active-fg)' : 'currentColor'}
                          strokeWidth={anyGcActive ? 2.5 : 2}
                        />
                      </span>
                      <span className="nav-label flex-1 flex items-center justify-between text-sm">
                        <span>{child.label}</span>
                        <Icon name={childOpen ? 'ChevronDown' : 'ChevronRight'} size={12} color="currentColor" strokeWidth={2} />
                      </span>
                    </button>
                    {childOpen && (
                      <div className="mt-0.5 ml-3 pl-3 border-l border-border space-y-0.5">
                        {accessibleGrandchildren.map((gc) => (
                          <ChildItem key={gc.id} child={gc} handleNavClick={handleNavClick} />
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              // Normal child leaf
              return <ChildItem key={child.id} child={child} handleNavClick={handleNavClick} />;
            })}
          </div>
        )}
      </div>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div className="sidebar-overlay lg:hidden" onClick={onClose} aria-hidden="true" />
      )}

      <aside
        className={`
          sidebar fixed top-0 left-0 h-full border-r flex flex-col
          transition-all duration-250 ease-out
          ${isCollapsed ? 'collapsed' : ''}
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        style={{ zIndex: 'var(--z-nav)', borderColor: 'var(--color-sidebar-border)' }}
        aria-label="Main navigation"
      >
        {/* Logo / Header */}
        <div className="sidebar-header">
          {isCollapsed ? (
            <div className="sidebar-logo">
              <Icon name="Landmark" size={20} color="rgba(255,255,255,0.80)" strokeWidth={2} />
            </div>
          ) : (
            <img
              src={LOGO}
              alt="SeedDrive logo"
              style={{ height: '34px', width: 'auto', maxWidth: '140px', filter: 'brightness(0) invert(1)' }}
              className="object-contain"
            />
          )}
          <button
            onClick={onToggleCollapse}
            className="hidden lg:flex ml-auto items-center justify-center w-7 h-7 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-250 flex-shrink-0"
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <Icon name={isCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={16} />
          </button>
        </div>

        {/* Context indicator — role-coloured identity zone */}
        {!isCollapsed ? (
          <div
            className="px-4 py-3 border-b border-l-4"
            style={{
              background: `linear-gradient(${roleProfile?.color}22, ${roleProfile?.color}22), var(--color-nav-context-bg)`,
              borderBottomColor: `${roleProfile?.color}35`,
              borderLeftColor: roleProfile?.color,
              boxShadow: `inset 0 0 20px ${roleProfile?.color}18`,
            }}
          >
            <p className="font-caption text-xs mb-0.5" style={{ color: roleProfile?.color, fontSize: '0.65rem', letterSpacing: '0.05em', textTransform: 'uppercase', opacity: 0.75 }}>Acting as</p>
            <p className="font-caption text-sm font-semibold leading-tight truncate" style={{ color: roleProfile?.color }}>
              {roleProfile?.label}
            </p>
            <p className="font-caption text-xs truncate" style={{ color: roleProfile?.color, opacity: 0.65 }}>
              {activeContext?.entity}
            </p>
          </div>
        ) : (
          <div
            className="flex justify-center py-3 border-b"
            style={{
              background: `linear-gradient(${roleProfile?.color}22, ${roleProfile?.color}22), var(--color-nav-context-bg)`,
              borderBottomColor: `${roleProfile?.color}35`,
              boxShadow: `inset 0 0 16px ${roleProfile?.color}18`,
            }}
          >
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-caption font-bold flex-shrink-0"
              style={{ background: roleProfile?.color, color: '#fff' }}
              title={`${roleProfile?.label} · ${activeContext?.entity}`}
            >
              {roleProfile?.initials}
            </div>
          </div>
        )}

        {/* Search + Pinned Shortcuts */}
        {!isCollapsed ? (
          <div className="px-3 pt-3 pb-2 border-b border-border space-y-2" style={{ background: 'var(--color-nav-panel)' }}>
            {/* Search bar */}
            <div className="relative">
              <button
                onClick={handleSearchToggle}
                className={`flex items-center gap-2 w-full px-3 py-2 rounded-md border text-sm transition-all duration-200 ${
                  searchOpen ? 'text-muted-foreground' : 'border-border text-muted-foreground hover:bg-white/10'
                }`}
                style={searchOpen
                  ? { background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.20)' }
                  : { background: 'rgba(255,255,255,0.06)' }
                }
                aria-label="Toggle search"
              >
                <Icon name="Search" size={15} color="currentColor" strokeWidth={2} />
                {!searchOpen && <span className="text-muted-foreground text-xs">Search navigation…</span>}
              </button>

              {searchOpen && (
                <div className="absolute inset-0 flex items-center rounded-md overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.20)' }}>
                  <span className="pl-3 flex-shrink-0">
                    <Icon name="Search" size={15} color="rgba(255,255,255,0.50)" strokeWidth={2} />
                  </span>
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search navigation…"
                    className="flex-1 px-2 py-2 text-xs bg-transparent outline-none text-foreground placeholder:text-muted-foreground"
                    aria-label="Search navigation"
                  />
                  <button onClick={handleSearchToggle} className="pr-2 text-muted-foreground hover:text-foreground" aria-label="Close search">
                    <Icon name="X" size={14} color="currentColor" />
                  </button>
                </div>
              )}

              {/* Search results */}
              {searchOpen && searchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-md shadow-lg overflow-hidden" style={{ zIndex: 'var(--z-dropdown)' }}>
                  {searchResults.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleSearchNav(item.path)}
                      className="flex items-center gap-2 w-full px-3 py-2 text-xs text-foreground hover:bg-muted transition-colors text-left"
                    >
                      <Icon name={item.icon} size={14} color="var(--color-primary)" strokeWidth={2} />
                      <span>{item.label}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* No results */}
              {searchOpen && searchQuery.trim().length > 0 && searchResults.length === 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-md shadow-lg px-3 py-2" style={{ zIndex: 'var(--z-dropdown)' }}>
                  <span className="text-xs text-muted-foreground">No results found</span>
                </div>
              )}

              {/* Search history */}
              {searchOpen && searchQuery.trim().length === 0 && searchHistory.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-md shadow-lg overflow-hidden" style={{ zIndex: 'var(--z-dropdown)' }}>
                  <div className="flex items-center justify-between px-3 pt-2 pb-1">
                    <span className="text-muted-foreground font-caption" style={{ fontSize: '0.65rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Recent searches</span>
                    <button onClick={handleClearHistory} className="text-xs text-muted-foreground hover:text-error transition-colors">Clear history</button>
                  </div>
                  <div className="flex flex-wrap gap-1.5 px-3 pb-2.5 pt-1">
                    {searchHistory.map((entry) => (
                      <span key={entry} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full bg-muted text-foreground text-xs font-medium">
                        <button onClick={() => { setSearchQuery(entry); setTimeout(() => searchInputRef.current?.focus(), 50); }} className="hover:text-primary transition-colors">{entry}</button>
                        <button
                          onClick={() => setSearchHistory((prev) => prev.filter((h) => h !== entry))}
                          className="flex items-center justify-center w-3.5 h-3.5 rounded-full hover:bg-muted-foreground/20 text-muted-foreground hover:text-foreground transition-colors"
                          aria-label={`Remove ${entry} from history`}
                        >
                          <Icon name="X" size={9} color="currentColor" strokeWidth={2.5} />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Pinned shortcuts */}
            <div>
              <p className="text-muted-foreground font-caption mb-1 px-1" style={{ fontSize: '0.65rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Quick Access</p>
              <div className="flex flex-col gap-0.5">
                {pinnedShortcuts.map((shortcut) => {
                  const active = isPathActive(location.pathname, shortcut.path);
                  return (
                    <button
                      key={shortcut.id}
                      onClick={() => handleNavClick(shortcut.path)}
                      className={`flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-xs font-medium transition-all duration-150 text-muted-foreground hover:bg-white/10 hover:text-white`}
                      style={active ? { background: 'var(--color-sidebar-active-bg)', color: 'var(--color-nav-active-fg)' } : {}}
                    >
                      <Icon name={shortcut.icon} size={13} color={active ? 'var(--color-nav-active-fg)' : 'currentColor'} strokeWidth={active ? 2.5 : 2} />
                      <span>{shortcut.label}</span>
                      {active && <span className="ml-auto w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: 'var(--color-nav-active-fg)' }} />}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          /* Collapsed: search icon + pin icons */
          <div className="flex flex-col items-center gap-1 py-2 border-b border-border">
            <div className="relative">
              <button
                onMouseEnter={() => setHoveredItem('__search__')}
                onMouseLeave={() => setHoveredItem(null)}
                className="flex items-center justify-center w-9 h-9 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150"
                aria-label="Search (expand sidebar to use)"
              >
                <Icon name="Search" size={17} color="currentColor" strokeWidth={2} />
              </button>
              {hoveredItem === '__search__' && (
                <div className="absolute left-full top-1/2 -translate-y-1/2 ml-3 px-3 py-1.5 bg-popover text-popover-foreground rounded-md shadow-md font-caption text-sm whitespace-nowrap pointer-events-none" style={{ zIndex: 'var(--z-dropdown)' }}>
                  Search
                </div>
              )}
            </div>
            {pinnedShortcuts.map((shortcut) => {
              const active = isPathActive(location.pathname, shortcut.path);
              return (
                <div key={shortcut.id} className="relative">
                  <button
                    onClick={() => handleNavClick(shortcut.path)}
                    onMouseEnter={() => setHoveredItem('__pin__' + shortcut.id)}
                    onMouseLeave={() => setHoveredItem(null)}
                    className="flex items-center justify-center w-9 h-9 rounded-md transition-all duration-150"
                    style={active ? { background: 'var(--color-sidebar-active-bg)', color: 'var(--color-nav-active-fg)' } : {}}
                    aria-label={shortcut.label}
                  >
                    <Icon name={shortcut.icon} size={17} color={active ? 'var(--color-nav-active-fg)' : 'currentColor'} strokeWidth={active ? 2.5 : 2} />
                  </button>
                  {hoveredItem === '__pin__' + shortcut.id && (
                    <div className="absolute left-full top-1/2 -translate-y-1/2 ml-3 px-3 py-1.5 bg-popover text-popover-foreground rounded-md shadow-md font-caption text-sm whitespace-nowrap pointer-events-none" style={{ zIndex: 'var(--z-dropdown)' }}>
                      {shortcut.label}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Primary Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1" aria-label="Primary navigation">
          {NAV_SECTIONS.map((section) => renderSection(section))}
        </nav>

        {/* Divider */}
        <div className="mx-3 border-t border-border" />

        {/* Bottom Navigation */}
        <nav className="py-4 px-3 space-y-1" aria-label="Secondary navigation">
          {BOTTOM_SECTIONS.filter((s) => canAccess(s.permission)).map((item) => {
            const active = isPathActive(location.pathname, item.path);
            return (
              <div key={item.id} className="relative">
                <button
                  onClick={() => handleNavClick(item.path)}
                  onMouseEnter={() => setHoveredItem(item.id)}
                  onMouseLeave={() => setHoveredItem(null)}
                  className={`sidebar-nav-item w-full text-left ${active ? 'active' : ''}`}
                  aria-current={active ? 'page' : undefined}
                  title={isCollapsed ? item.label : undefined}
                >
                  <span className="flex-shrink-0">
                    <Icon name={item.icon} size={20} color={active ? 'var(--color-nav-active-fg)' : 'currentColor'} strokeWidth={active ? 2.5 : 2} />
                  </span>
                  <span className="nav-label">{item.label}</span>
                </button>
                {isCollapsed && hoveredItem === item.id && (
                  <div className="absolute left-full top-1/2 -translate-y-1/2 ml-3 px-3 py-1.5 bg-popover text-popover-foreground rounded-md shadow-md font-caption text-sm whitespace-nowrap pointer-events-none" style={{ zIndex: 'var(--z-dropdown)' }}>
                    {item.label}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* User Profile Footer */}
        <div className="border-t border-border p-3" style={{ background: 'rgba(0,0,0,0.22)' }}>
          <button
            className="flex items-center gap-3 w-full p-2 rounded-md hover:bg-muted transition-all duration-250 text-left min-h-touch"
            title={isCollapsed ? 'User Profile' : undefined}
          >
            <div
              className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full font-caption font-semibold text-sm text-white"
              style={{ background: roleProfile?.color || 'var(--color-primary)' }}
            >
              JD
            </div>
            {!isCollapsed && (
              <>
                <div className="flex-1 min-w-0">
                  <p className="font-caption font-medium text-foreground text-sm truncate">James Donovan</p>
                  <p className="font-caption text-muted-foreground truncate" style={{ fontSize: '0.75rem' }}>{roleProfile?.label}</p>
                </div>
                <Icon name="LogOut" size={16} color="var(--color-muted-foreground)" />
              </>
            )}
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-2 py-1.5 mt-1 rounded-md hover:bg-error/10 transition-all duration-250 text-left text-muted-foreground hover:text-error"
            title={isCollapsed ? 'Log Out' : undefined}
            aria-label="Log out"
          >
            <span className="flex-shrink-0">
              <Icon name="LogOut" size={18} color="currentColor" />
            </span>
            {!isCollapsed && <span className="nav-label font-caption text-sm font-medium">Log Out</span>}
          </button>
        </div>
      </aside>

      {/* Toast */}
      {toastMsg && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 px-4 py-2 bg-foreground text-background rounded-md shadow-lg text-sm font-medium pointer-events-none"
          style={{ zIndex: 'var(--z-toast, 9999)' }}
        >
          {toastMsg}
        </div>
      )}
    </>
  );
}
